import React, { useState, useEffect } from 'react';
import { createConversion, updateConversion } from '../../services/conversionsService';

const ConversionForm = ({ isOpen, onClose, conversion = null, units = [], onSuccess }) => {
  const [formData, setFormData] = useState({
    from_unit_id: '',
    to_unit_id: '',
    conversion_rate: '',
    description: '',
    is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (conversion) {
      // Modo edición
      setFormData({
        from_unit_id: conversion.from_unit_id || '',
        to_unit_id: conversion.to_unit_id || '',
        conversion_rate: conversion.conversion_rate || '',
        description: conversion.description || '',
        is_active: conversion.is_active !== undefined ? conversion.is_active : true,
      });
    } else {
      // Modo creación
      setFormData({
        from_unit_id: '',
        to_unit_id: '',
        conversion_rate: '',
        description: '',
        is_active: true,
      });
    }
    setErrors({});
  }, [conversion, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let nextValue = type === 'checkbox' ? checked : value;
    if (name === 'conversion_rate' && type !== 'checkbox') {
      // Máximo 4 dígitos en la parte entera y 4 decimales.
      const cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '');
      const [intRaw, ...rest] = cleaned.split('.');
      const intPart = (intRaw || '').slice(0, 4);
      const decPart = rest.join('').slice(0, 4);
      nextValue = decPart.length > 0 ? `${intPart}.${decPart}` : intPart;
    }

    setFormData(prev => ({
      ...prev,
      [name]: nextValue,
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.from_unit_id) {
      newErrors.from_unit_id = 'La unidad de origen es obligatoria';
    }

    if (!formData.to_unit_id) {
      newErrors.to_unit_id = 'La unidad de destino es obligatoria';
    }

    if (formData.from_unit_id === formData.to_unit_id) {
      newErrors.to_unit_id = 'La unidad de destino debe ser diferente a la unidad de origen';
    }

    if (!formData.conversion_rate || parseFloat(formData.conversion_rate) <= 0) {
      newErrors.conversion_rate = 'La tasa de conversión debe ser mayor a 0';
    }
    const [intPart = ''] = String(formData.conversion_rate || '').split('.');
    if (intPart.length > 4) {
      newErrors.conversion_rate = 'La tasa de conversión admite máximo 4 dígitos enteros';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      let response;
      const submitData = {
        ...formData,
        conversion_rate: parseFloat(formData.conversion_rate),
        from_unit_id: parseInt(formData.from_unit_id),
        to_unit_id: parseInt(formData.to_unit_id),
      };

      if (conversion) {
        response = await updateConversion(conversion.id, submitData);
      } else {
        response = await createConversion(submitData);
      }

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        // Manejar errores del servidor
        if (response.errors) {
          setErrors(response.errors);
        } else {
          setErrors({ submit: response.message || 'Error al guardar la conversión' });
        }
      }
    } catch (error) {
      console.error('Error al guardar conversión:', error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: error.response?.data?.message || 'Error al guardar la conversión' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Filtrar unidades disponibles (excluir la unidad seleccionada en el otro campo)
  const availableFromUnits = units.filter(u => 
    !formData.to_unit_id || u.id !== parseInt(formData.to_unit_id)
  );
  const availableToUnits = units.filter(u => 
    !formData.from_unit_id || u.id !== parseInt(formData.from_unit_id)
  );

  // Calcular vista previa
  const previewData = (() => {
    if (!formData.from_unit_id || !formData.to_unit_id || !formData.conversion_rate) {
      return null;
    }
    
    const fromUnit = units.find(u => u.id === parseInt(formData.from_unit_id));
    const toUnit = units.find(u => u.id === parseInt(formData.to_unit_id));
    const rate = parseFloat(formData.conversion_rate);
    
    if (!fromUnit || !toUnit || isNaN(rate) || rate <= 0) {
      return null;
    }
    
    return { fromUnit, toUnit, rate };
  })();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              {conversion ? 'Editar Conversión' : 'Nueva Conversión'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {errors.submit && (
            <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md text-sm border border-red-200">
              {errors.submit}
            </div>
          )}

          {/* Unidad Origen */}
          <div className="mb-4">
            <label htmlFor="from_unit_id" className="block text-sm font-medium text-gray-700 mb-1">
              Unidad de Origen <span className="text-red-500">*</span>
            </label>
            <select
              id="from_unit_id"
              name="from_unit_id"
              value={formData.from_unit_id}
              onChange={handleChange}
              disabled={!!conversion}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.from_unit_id
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              } ${conversion ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">Seleccione una unidad</option>
              {availableFromUnits.map(unit => (
                <option key={unit.id} value={unit.id}>
                  {unit.name} ({unit.symbol})
                </option>
              ))}
            </select>
            {errors.from_unit_id && (
              <p className="mt-1 text-sm text-red-600">{errors.from_unit_id}</p>
            )}
            {conversion && (
              <p className="mt-1 text-xs text-gray-500">La unidad de origen no se puede modificar</p>
            )}
          </div>

          {/* Unidad Destino */}
          <div className="mb-4">
            <label htmlFor="to_unit_id" className="block text-sm font-medium text-gray-700 mb-1">
              Unidad de Destino <span className="text-red-500">*</span>
            </label>
            <select
              id="to_unit_id"
              name="to_unit_id"
              value={formData.to_unit_id}
              onChange={handleChange}
              disabled={!!conversion}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.to_unit_id
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              } ${conversion ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">Seleccione una unidad</option>
              {availableToUnits.map(unit => (
                <option key={unit.id} value={unit.id}>
                  {unit.name} ({unit.symbol})
                </option>
              ))}
            </select>
            {errors.to_unit_id && (
              <p className="mt-1 text-sm text-red-600">{errors.to_unit_id}</p>
            )}
            {conversion && (
              <p className="mt-1 text-xs text-gray-500">La unidad de destino no se puede modificar</p>
            )}
          </div>

          {/* Tasa de Conversión */}
          <div className="mb-4">
            <label htmlFor="conversion_rate" className="block text-sm font-medium text-gray-700 mb-1">
              Tasa de Conversión <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="conversion_rate"
              name="conversion_rate"
              value={formData.conversion_rate}
              onChange={handleChange}
              step="0.0001"
              min="0.0001"
              max="9999.9999"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.conversion_rate
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              }`}
              placeholder="Ej: 1000 (1 KG = 1000 G)"
            />
            {errors.conversion_rate && (
              <p className="mt-1 text-sm text-red-600">{errors.conversion_rate}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Ejemplo: Si 1 Kilogramo = 1000 Gramos, ingrese 1000
            </p>
          </div>

          {/* Vista Previa del Cálculo */}
          {previewData && (
            <div className="mb-4 bg-teal-50 border border-teal-200 rounded-lg p-4">
              <div className="flex items-center justify-center gap-3">
                <div className="text-center">
                  <div className="text-2xl font-bold text-teal-700">1</div>
                  <div className="text-sm font-medium text-teal-600 mt-1">
                    {previewData.fromUnit.name} ({previewData.fromUnit.symbol})
                  </div>
                </div>
                <div className="text-2xl text-teal-600 font-bold">=</div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-teal-700">
                    {previewData.rate.toLocaleString('es-ES', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 4
                    })}
                  </div>
                  <div className="text-sm font-medium text-teal-600 mt-1">
                    {previewData.toUnit.name} ({previewData.toUnit.symbol})
                  </div>
                </div>
              </div>
              <div className="mt-3 text-center">
                <p className="text-xs text-teal-600 italic">
                  1 {previewData.fromUnit.symbol} = {previewData.rate.toLocaleString('es-ES', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4
                  })} {previewData.toUnit.symbol}
                </p>
              </div>
            </div>
          )}

          {/* Descripción */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Descripción opcional de la conversión"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Estado Activo */}
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="ml-2 text-sm text-gray-700">Conversión activa</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Guardando...' : conversion ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConversionForm;
